import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { StatusChip } from 'components/referrals/referralsTableColumns';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import InfoChip from 'components/shared/InfoChip';
import Menu from 'components/shared/menu';
import { useProfile } from 'context/profile';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { simpleDateAtTime } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import { ReviewStatus } from 'models/Review';
import Admission from 'models/reviews/Admission';
import { updateReview } from 'services/api/reviews';
import { colors } from 'styles/theme/colors';
import { H3, Label } from 'styles/typography';
import EllipsisIcon from 'svg/EllipsisIcon';
import PencilIcon from 'svg/PencilIcon';
import TrashIcon from 'svg/TrashIcon';

type Props = {
  admission: Admission;
  onDeny: () => void;
  onEditPatient: () => void;
  onDeleteSummary: () => void;
  closeButton: ReactNode;
};

export default function AdmissionSlideoutHeader({
  admission,
  onDeny,
  onEditPatient,
  onDeleteSummary,
  closeButton,
}: Props) {
  const { profile } = useProfile();
  const invalidateQueries = useInvalidateQueries();
  const canEdit = profile.has(PERMISSIONS.reviewAdmissionEdit) && admission.pending;
  const canDelete = profile.has(PERMISSIONS.reviewAdmissionDelete);

  const { mutate: acceptAdmission, isPending: isAccepting } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: () => {
      invalidateQueries({ admissions: true });
    },
  });

  const patientName = admission.data.patient?.name;
  const referralSource = admission.referredBy?.name ?? admission.data.referralSource;
  const groupName = admission.group?.name;
  const planType = admission.data.planType;

  const descriptionNode = useMemo(() => {
    const groupPart = groupName ? `Referral to ${groupName} ` : '';
    const relevantDate = admission.pending ? admission.createdAt : admission.updatedAt;
    const timestamp = relevantDate ? simpleDateAtTime(relevantDate) : null;

    if (admission.accepted) {
      const reviewedByName = admission.reviewedBy?.fullName;
      return (
        <>
          {groupPart}
          <StatusText $variant='accepted'>accepted</StatusText>
          {reviewedByName && ` by ${reviewedByName}`}
          {timestamp && ` on ${timestamp}`}
        </>
      );
    }

    if (admission.rejected) {
      const reviewedByName = admission.reviewedBy?.fullName;
      const denialReason = admission.denialReasonOptions
        .find((option) => option.apiName === admission.data.denialReasonApiName)
        ?.displayName.toLowerCase();
      return (
        <>
          {groupPart}
          <StatusText $variant='denied'>denied{denialReason && ` (${denialReason})`}</StatusText>
          {reviewedByName && ` by ${reviewedByName}`}
          {timestamp && ` on ${timestamp}`}
        </>
      );
    }

    const createdByName = admission.createdBy?.fullName;
    return (
      <>
        {groupPart}
        {createdByName && `created by ${createdByName}`}
        {timestamp && ` on ${timestamp}`}
      </>
    );
  }, [admission, groupName]);

  return (
    <Header>
      <HeaderContent>
        <TitleRow>
          <TitleSection>
            <NameRow>
              <PatientName>{patientName || <>&mdash;</>}</PatientName>
              {!admission.pending && <StatusChip $status={admission.status}>{admission.statusLabel}</StatusChip>}
            </NameRow>
            {descriptionNode && <Description>{descriptionNode}</Description>}
          </TitleSection>
          <Actions>
            <AccessGuard accessor={PERMISSIONS.reviewAdmissionEdit}>
              {admission.pending && (
                <>
                  <Button
                    variant='call-to-action'
                    size='small'
                    loading={isAccepting}
                    onClick={() =>
                      acceptAdmission({
                        id: admission.id,
                        status: ReviewStatus.ACCEPTED,
                        reviewedById: profile?.id,
                      })
                    }>
                    Accept
                  </Button>
                  <Button variant='danger' size='small' disabled={isAccepting} onClick={onDeny}>
                    Deny
                  </Button>
                </>
              )}
            </AccessGuard>
            {(canEdit || canDelete) && (
              <Menu $size='large'>
                <Menu.Trigger>
                  <MoreButton variant='outline-gray' size='small' aria-label='More actions'>
                    <EllipsisIcon />
                  </MoreButton>
                </Menu.Trigger>
                <Menu.Content position='right' style={{ marginTop: 4, zIndex: 1200 }}>
                  {canEdit && (
                    <StyledMenuItem onClick={onEditPatient}>
                      <PencilIcon color='transparent' stroke={colors.black} strokeWidth={1.5} />
                      Edit Patient Information
                    </StyledMenuItem>
                  )}
                  {canEdit && canDelete && <Menu.Divider />}
                  {canDelete && (
                    <StyledMenuItem onClick={onDeleteSummary} style={{ color: colors.accentRed }}>
                      <TrashIcon width={14} height={14} />
                      Delete Referral
                    </StyledMenuItem>
                  )}
                </Menu.Content>
              </Menu>
            )}
          </Actions>
        </TitleRow>

        {(planType || referralSource) && (
          <ChipRow>
            {planType && <InfoChip $size='small'>Plan Type: {planType}</InfoChip>}
            {referralSource && <InfoChip $size='small'>Referral Source: {referralSource}</InfoChip>}
          </ChipRow>
        )}
      </HeaderContent>

      {closeButton}
    </Header>
  );
}

const Header = styled.div`
  display: flex;
  padding: 24px 40px 0;
  position: relative;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
  padding-right: 40px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PatientName = styled(H3)`
  font-size: 16px;
`;

const Description = styled(Label)`
  color: ${colors.black75};
`;

const StatusText = styled.span<{ $variant: 'accepted' | 'denied' }>`
  color: ${({ $variant }) => ($variant === 'accepted' ? colors.accentGreen : colors.accentRed)};
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const MoreButton = styled(Button)`
  padding: 8px 12px;
  height: 37px;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StyledMenuItem = styled(Menu.Item)`
  gap: 12px;
`;
